import React, { useEffect, useMemo } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import Routes from '@src/routes'

import { setRoom } from '@store/actionSlices/appConfig'
import { setFirebaseSession } from '@store/actionSlices/firestore'
import { setProjectIdentity } from '@store/actionSlices/projectIdentity'
import { TokenInterface } from '@store/actionSlices/token'
import { DEFAULT_SHOWCASE_ROOM, RouteKey } from '@store/constants'
import type { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import IdleTimeHandler from '@components/idle-time-handler'

import useConnectionIndicator from '@utilities/connection-indicator'
import { getQueryStringParams } from '@utilities/helper'
import RefreshHandler from '@utilities/refresh-handler'
import styleUtil from '@utilities/style-util'
import TokenRenewalHandler from '@utilities/token-renewal-handler'

import useFirebaseCacheVersionService from '@firebaseUtil/firebase-cache-version-service'
import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import initFirebaseSnapshot from '@firebaseUtil/firebase-snapshot'
import { SessionMap } from '@firebaseUtil/types'

import useMasterKeyHandler from '@hooks/useMasterKeyHandler'

import '@adUtilities/sentry'
import { hasToken } from '@adUtilities/token-helper'

import TemplateHandler from './components/template-handler'
import VisionSkeletonV2 from './pages/vision/v2/vision-skeleton'
import VisionSkeleton from './pages/vision/vision-skeleton'
import { selectDisplayScreens } from './store/actionSlices/multiScreen'

interface AppProps {
  session: SessionMap | undefined
  projectIdentity: ProjectIdentity
  token: TokenInterface
  isOnline: boolean
  remoteRefreshTriggerKey: string
  showcaseRoom?: string
}

function App({
  session,
  projectIdentity,
  token,
  isOnline,
  remoteRefreshTriggerKey,
  showcaseRoom,
}: AppProps) {
  styleUtil(projectIdentity.theme)
  const location = useLocation()
  const dispatch = useDispatch()

  const multiScreenDisplays = useSelector(selectDisplayScreens)

  const { access_token } = getQueryStringParams(location.search)

  useConnectionIndicator({ token, isOnline })
  TokenRenewalHandler({ token })

  const firebaseSession = initFirebaseSnapshot(
    'devsuite',
    projectIdentity.projectId,
    'session',
    projectIdentity.sessionId
  )

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  useFirebaseCacheVersionService(
    projectIdentity.projectName,
    Boolean(hasToken(token) && projectIdentity.projectName)
  )

  React.useEffect(() => {
    if (showcaseRoom?.toLowerCase() === DEFAULT_SHOWCASE_ROOM) {
      dispatch(setRoom(''))
    }
  }, [])

  useEffect(() => {
    if (!access_token && firebaseSession) {
      dispatch(setFirebaseSession(firebaseSession as SessionMap))
    }
  }, [firebaseSession])

  const isMasterKeyLoginLoading = useMasterKeyHandler()

  RefreshHandler({
    refreshTriggerKeyFirebase: session?.remoteRefreshTriggerKey ?? '',
    refreshTriggerKeyLocal: remoteRefreshTriggerKey,
  })

  const handleClientCheck = (client: boolean) => {
    if (!client) {
      firebaseControlQuery
        .update({
          [`client.remote`]: true,
        })
        .catch((err) => console.error('Client check encountered an error', err))
    }
  }

  const shouldBlockIdleTimeHandler = useMemo(
    () => Boolean(projectIdentity.masterKey && multiScreenDisplays.length > 0),
    [projectIdentity.masterKey, multiScreenDisplays]
  )

  useEffect(() => {
    if (session) {
      const {
        client: { remote },
      } = session
      handleClientCheck(remote)
    }
  }, [session])

  useEffect(() => {
    const queryParams = getQueryStringParams(location.search)
    if (!queryParams) return

    const updatedIdentity = {
      ...projectIdentity,
      disconnect:
        queryParams.disconnect !== undefined
          ? queryParams.disconnect === 'true'
          : projectIdentity.disconnect,
      showClearCacheOption:
        queryParams.clearCache !== undefined
          ? queryParams.clearCache === 'true'
          : projectIdentity.showClearCacheOption,
    }

    dispatch(setProjectIdentity(updatedIdentity))
  }, [])

  if (
    isMasterKeyLoginLoading &&
    location.pathname !== `/${RouteKey.DASHBOARD}`
  ) {
    return <TemplateHandler v1={<VisionSkeleton />} v2={<VisionSkeletonV2 />} />
  }

  return (
    <>
      <Routes />
      {!shouldBlockIdleTimeHandler && <IdleTimeHandler />}
    </>
  )
}

export default connect(
  ({
    projectIdentity,
    token,
    statusIndicator: { isOnline },
    firestore: { session },
    appConfig: { remoteRefreshTriggerKey, room: showcaseRoom },
  }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
    token,
    isOnline,
    remoteRefreshTriggerKey,
    showcaseRoom,
  })
)(App)
